// src/pages/university-admin/StudentApproval.js

import React, { useState, useEffect, useRef } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    Box,
    Grid,
    Card,
    CardContent,
    Avatar,
    Chip,
    LinearProgress,
    useTheme,
    Tooltip,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    useMediaQuery,
    Stack,
    Switch,
    FormControlLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    IconButton
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Search,
    School,
    Work,
    Visibility,
    People,
    Email,
    Phone,
    Flag,
    Person,
    Badge as BadgeIcon,
    Wc
} from '@mui/icons-material';
import useCustomToast from '../../hooks/useToast';
import {
    APIStudentApprovalUpdateApproval,
    APIStudentApprovalShow,
    APIStudentProfileImage,
    APIStudentApprovalByUniversity
} from '../../apiServices/APIs';
import { 
    formatCreatedAtDate, 
    getUserDetails
} from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorState';
// NOTE: The component file is still named InstitutionAdminBar.js
// but it now renders "Unit Research Lead Portal" as its visible title.
import InstitutionAdminBar from '../../components/InstitutionAdminBar';
import { FullWidthContainer } from '../../components/FullWidthContainer';

const getInitials = (name) => {
    if (!name) return 'ST';
    return name
        .split(' ')
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2);
};

const stringToColor = (string) => {
    if (!string) return '#1976d2';
    let hash = 0;
    for (let i = 0; i < string.length; i++) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (let i = 0; i < 3; i++) {
        const value = (hash >> (i * 8)) & 0xFF;
        color += ('00' + value.toString(16)).substr(-2);
    }
    return color;
};

const StudentApproval = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [approvals, setApprovals] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedApproval, setSelectedApproval] = useState(null);
    const [openViewDialog, setOpenViewDialog] = useState(false);
    const showToast = useCustomToast();
    const isMounted = useRef(true);
    const adminId = getUserDetails("Id");

    useEffect(() => {
        isMounted.current = true;
        apiService.setToastFunction(showToast);
        fetchAllData();
        
        return () => {
            isMounted.current = false;
        };
    }, []);

    const fetchAllData = async () => {
        setLoading(true);
        setError(null);
        
        try {
            await fetchApprovals();
        } catch (err) {
            setError("Failed to load approval data");
            console.error("Fetch error:", err);
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    const fetchApprovals = async () => {
        const universityId = getUserDetails("universityId");
        const result = await apiService.listById(APIStudentApprovalByUniversity, universityId);
        if (result.success && isMounted.current) {
            setApprovals(Array.isArray(result.data) ? result.data : []);
        } else {
            console.error("Failed to fetch approvals:", result.message);
        }
    };

    const handleStatusToggle = async (approval, field) => {
        const formDataToSend = new FormData();
        formDataToSend.append('admin_id', adminId);
        formDataToSend.append('approval_id', approval.id);
        
        if (field === 'is_verified') {
            const currentVal = approval.student?.is_verified;
            formDataToSend.append('is_verified', !currentVal ? '1' : '0');
        } else if (field === 'status') {
            const currentVal = approval.student?.status === 'active';
            formDataToSend.append('status', !currentVal ? '1' : '0');
        }

        try {
            const result = await apiService.update(APIStudentApprovalUpdateApproval, formDataToSend);
            if (result && result.success) {
                showToast("Success", "Status updated successfully", "success");
                fetchAllData();
            } else {
                showToast("Error", result?.message || "Operation failed", "error");
            }
        } catch (error) {
            showToast("Error", "Internal connection error", "error");
        }
    };

    const handleOpenViewDialog = async (approval) => {
        setSelectedApproval(approval);
        setOpenViewDialog(true);
        
        try {
            const result = await apiService.show(APIStudentApprovalShow, approval.id);
            if (result.success && result.data) {
                setSelectedApproval(result.data);
            }
        } catch (error) {
            console.error("Error fetching details:", error);
        }
    };

    const handleCloseViewDialog = () => {
        setOpenViewDialog(false);
        setSelectedApproval(null);
    };

    const getProfileImageUrl = (student) => {
        if (student?.profile_image) {
            return APIStudentProfileImage + student.profile_image;
        }
        return null;
    };

    const filteredApprovals = approvals.filter(approval => 
        approval.student?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        approval.student?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        approval.approval_id?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const pendingCount = approvals.filter(a => !a.student?.is_verified).length;

    if (loading) {
        return <LoadingState message="Loading Student Approvals..." variant="research" size="large" fullPage />;
    }

    if (error) {
        return <ErrorState error={error} onRetry={fetchAllData} title="Unable to Load Approvals" variant="research" fullPage retryText="Retry Loading" />;
    }

    return (
        <>
            <FullWidthContainer >
                <Box sx={{ backgroundColor: 'white', borderRadius: 3 }}>
                    <InstitutionAdminBar notificationCount={90} />
                    
                    {/* Header Section */}
                    <Paper elevation={2} sx={{ 
                        p: { xs: 2, sm: 3 }, 
                        mb: { xs: 2, sm: 3 }, 
                        borderRadius: 3,
                        backgroundColor: 'white',
                    }}>
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            flexWrap: 'wrap', 
                            gap: 2,
                            flexDirection: { xs: 'column', sm: 'row' }
                        }}>
                            <Typography variant="h6" sx={{ 
                                fontWeight: 'bold', 
                                color: theme.palette.primary.main, 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1,
                                fontSize: { xs: '1rem', sm: '1.25rem' }
                            }}>
                                <People /> Student Approval Queue
                                {pendingCount > 0 && (
                                    <Chip 
                                        label={`${pendingCount} Pending`} 
                                        color="warning" 
                                        size="small"
                                        sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                    />
                                )}
                            </Typography>
                            <Button 
                                variant="outlined" 
                                color="info" 
                                startIcon={<RefreshIcon />} 
                                onClick={fetchAllData} 
                                sx={{ 
                                    textTransform: 'none',
                                    width: { xs: '100%', sm: 'auto' }
                                }}
                                size={isMobile ? "small" : "medium"}
                            >
                                Refresh
                            </Button>
                        </Box>
                    </Paper>

                    {/* Search Bar */}
                    <Paper sx={{ 
                        mb: 3, 
                        p: { xs: 1.5, sm: 2 }, 
                        display: 'flex', 
                        alignItems: 'center',
                        backgroundColor: theme.palette.grey[50],
                        borderRadius: 2
                    }}>
                        <Search sx={{ mx: 1, color: 'text.secondary' }} />
                        <TextField 
                            fullWidth 
                            placeholder="Search by name, email, or ID..." 
                            variant="standard" 
                            value={searchTerm} 
                            onChange={(e) => setSearchTerm(e.target.value)} 
                            InputProps={{ disableUnderline: true }}
                            size={isMobile ? "small" : "medium"}
                        />
                    </Paper>
                </Box>
                
                {/* Approvals Table */}
                <Paper elevation={2} sx={{ 
                    borderRadius: 3, 
                    overflow: 'auto',
                    backgroundColor: 'white',
                }}>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table sx={{ minWidth: { xs: 600, sm: 700, md: 900 } }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: theme.palette.primary.main }}>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Applicant</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Gender</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>University</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Submitted</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Profile Completion</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Approved (Verified)</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Account Status</TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredApprovals.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((approval) => {
                                    const avatarColor = stringToColor(approval.student?.name);
                                    const initials = getInitials(approval.student?.name);
                                    const profileImage = getProfileImageUrl(approval.student);
                                    
                                    return (
                                        <TableRow key={approval.id} hover>
                                            <TableCell>
                                                <Box sx={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    gap: 2,
                                                    flexDirection: { xs: 'column', sm: 'row' },
                                                    textAlign: { xs: 'center', sm: 'left' }
                                                }}>
                                                    <Avatar 
                                                        src={profileImage}
                                                        sx={{ 
                                                            width: { xs: 40, sm: 48 }, 
                                                            height: { xs: 40, sm: 48 },
                                                            bgcolor: !profileImage ? avatarColor : 'transparent',
                                                            fontWeight: 'bold'
                                                        }}
                                                    >
                                                        {!profileImage && initials}
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="body2" sx={{ 
                                                            fontWeight: 600,
                                                            fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                                        }}>
                                                            {approval.student?.name}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <Email sx={{ fontSize: 12 }} />
                                                            {approval.student?.email}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip 
                                                    icon={<Wc fontSize="small" />}
                                                    label={approval.student?.gender ? approval.student.gender.charAt(0).toUpperCase() + approval.student.gender.slice(1) : 'N/A'} 
                                                    size="small" 
                                                    color="primary" 
                                                    variant="outlined"
                                                    sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                                                {approval.student?.university}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                                                {formatCreatedAtDate(approval.submission_date)}
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                    <LinearProgress 
                                                        variant="determinate" 
                                                        value={approval.verification?.progress || 0} 
                                                        sx={{ width: { xs: 60, sm: 80 }, height: 6, borderRadius: 3 }} 
                                                    />
                                                    <Typography variant="caption" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                                        {approval.verification?.progress || 0}%
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            size="small"
                                                            checked={approval.student?.is_verified || false}
                                                            onChange={() => handleStatusToggle(approval, 'is_verified')}
                                                            color="success"
                                                        />
                                                    }
                                                    label={approval.student?.is_verified ? "Approved" : "Pending"}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            size="small"
                                                            checked={approval.student?.status === 'active'}
                                                            onChange={() => handleStatusToggle(approval, 'status')}
                                                            color="primary"
                                                        />
                                                    }
                                                    label={approval.student?.status === 'active' ? "Active" : "Suspended"}
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap">
                                                    <Tooltip title="View Details">
                                                        <IconButton size="small" onClick={() => handleOpenViewDialog(approval)}>
                                                            <Visibility fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                                {filteredApprovals.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                No student approvals found
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={filteredApprovals.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(e, p) => setPage(p)}
                        onRowsPerPageChange={(e) => { 
                            setRowsPerPage(parseInt(e.target.value, 10)); 
                            setPage(0); 
                        }}
                        sx={{
                            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                fontSize: { xs: '0.7rem', sm: '0.875rem' }
                            }
                        }}
                    />
                </Paper>

                {/* View Details Dialog */}
                <Dialog 
                    open={openViewDialog} 
                    onClose={handleCloseViewDialog} 
                    maxWidth="md" 
                    fullWidth
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' },
                            maxHeight: '90vh'
                        }
                    }}
                >
                    <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, borderBottom: `1px solid ${theme.palette.divider}` }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <People color="primary" />
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                    Student Application Details
                                </Typography>
                            </Box>
                            <Chip 
                                label={selectedApproval?.student?.is_verified ? "Verified" : "Pending Verification"} 
                                color={selectedApproval?.student?.is_verified ? "success" : "warning"}
                                size="small"
                            />
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        {selectedApproval && selectedApproval.student && (
                            <Box sx={{ pt: 2 }}>
                                {/* Profile Header */}
                                <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 2, 
                                    mb: 3,
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    textAlign: { xs: 'center', sm: 'left' }
                                }}>
                                    <Avatar 
                                        src={getProfileImageUrl(selectedApproval.student)}
                                        sx={{ 
                                            width: { xs: 64, sm: 80 }, 
                                            height: { xs: 64, sm: 80 },
                                            bgcolor: !getProfileImageUrl(selectedApproval.student) ? stringToColor(selectedApproval.student.name) : 'transparent',
                                            fontSize: '2rem',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        {!getProfileImageUrl(selectedApproval.student) && getInitials(selectedApproval.student.name)}
                                    </Avatar>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 'bold' }}>
                                            {selectedApproval.student.name}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Email sx={{ fontSize: 14 }} />
                                            {selectedApproval.student.email}
                                        </Typography>
                                        <Typography variant="caption" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                            <School fontSize="small" />
                                            {selectedApproval.student.university || 'University Not Assigned'}
                                        </Typography>
                                    </Box>
                                </Box>
                                
                                <Divider sx={{ my: 2 }} />
                                
                                {/* Personal Info */}
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                    Personal Information
                                </Typography>
                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Person fontSize="small" color="action" />
                                            <strong>Full Name:</strong> {selectedApproval.student.name}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Phone fontSize="small" color="action" />
                                            <strong>Phone:</strong> {selectedApproval.student.contact_phone || 'N/A'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Flag fontSize="small" color="action" />
                                            <strong>Nationality:</strong> {selectedApproval.student.nationality || 'N/A'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Wc fontSize="small" color="action" />
                                            <strong>Gender:</strong> {selectedApproval.student.gender ? selectedApproval.student.gender.charAt(0).toUpperCase() + selectedApproval.student.gender.slice(1) : 'N/A'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <BadgeIcon fontSize="small" color="action" />
                                            <strong>Registration ID:</strong> {selectedApproval.student.registration_id}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <School fontSize="small" color="action" />
                                            <strong>University:</strong> {selectedApproval.student.university || 'N/A'}
                                        </Typography>
                                    </Grid>
                                </Grid>
                                
                                {/* Biography */}
                                {selectedApproval.student.biography && (
                                    <>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                            Biography
                                        </Typography>
                                        <Paper sx={{ p: 2, bgcolor: theme.palette.grey[50], borderRadius: 2 }}>
                                            <Typography variant="body2" sx={{ lineHeight: 1.6, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                {selectedApproval.student.biography}
                                            </Typography>
                                        </Paper>
                                    </>
                                )}
                                
                                {/* Verification Progress */}
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, mt: 2, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                    Verification Progress
                                </Typography>
                                <Grid container spacing={1} sx={{ mb: 2 }}>
                                    <Grid item xs={12} sm={6}>
                                        <Chip 
                                            icon={<School />} 
                                            label={`Qualifications: ${!empty(selectedApproval.student.biography) ? 'Verified' : 'Not Started'}`} 
                                            color={!empty(selectedApproval.student.biography) ? 'success' : 'default'} 
                                            size="small"
                                            sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Chip 
                                            icon={<Work />} 
                                            label={`Experience: ${!empty(selectedApproval.student.contact_phone) ? 'Verified' : 'Not Started'}`} 
                                            color={!empty(selectedApproval.student.contact_phone) ? 'success' : 'default'} 
                                            size="small"
                                            sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
                        <Button onClick={handleCloseViewDialog} variant="contained" size={isMobile ? "small" : "medium"}>
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
            </FullWidthContainer>
        </>
    );
};

// Simple utility function helper
const empty = (val) => {
    return !val || val === '' || val.length === 0;
};

export default StudentApproval;